import { Box, chakra } from "@chakra-ui/react";
import { NavLink, useLocation } from "react-router-dom";
import { Icon, type IconName } from "../../components/Icon/Icon";
import { MODES, useWorkspace, type Workspace } from "../../lib/useWorkspace";

type NavItem = {
  to: string;
  label: string;
  icon: IconName;
  isActive?: (path: string) => boolean;
};

const ITEMS: NavItem[] = [
  {
    to: "/spark",
    label: "spark",
    icon: "spark",
    isActive: (path) => path === "/spark",
  },
  {
    to: "/events",
    label: "home",
    icon: "logo",
    isActive: (path) =>
      path === "/events" ||
      path.startsWith("/spark/atlanta/") ||
      path.startsWith("/event/atlanta/"),
  },
  { to: "/matches", label: "matches", icon: "chat" },
];

// The tab for the person's workspace (see useWorkspace). Hosts get their
// check-in list; admins reach check-in from the admin event page instead, so
// they aren't given a fifth tab.
const ROLE_ITEM: Record<Workspace, NavItem> = {
  admin: {
    to: "/admin",
    label: "admin",
    icon: MODES.admin.icon,
    isActive: (path) => path.startsWith("/admin"),
  },
  host: {
    to: "/host",
    label: "check-in",
    icon: MODES.host.icon,
    isActive: (path) => path.startsWith("/host"),
  },
};

export function BottomNav() {
  const { pathname } = useLocation();
  const path = pathname.toLowerCase();
  const workspace = useWorkspace();
  const roleItem = workspace && ROLE_ITEM[workspace];
  const items = roleItem ? [...ITEMS, roleItem] : ITEMS;

  return (
    <Box
      as="nav"
      position="fixed"
      left="0"
      right="0"
      bottom="0"
      bg="bg.brand"
      boxShadow="bottomNav"
      display={{ base: "flex", md: "none" }}
      justifyContent="space-around"
      alignItems="center"
      h="65px"
      pt="1"
      pb="env(safe-area-inset-bottom)"
      zIndex={11}
    >
      {items.map(({ to, label, icon, isActive }) => (
        <NavLink
          key={to}
          to={to}
          style={{ flex: 1, textDecoration: "none" }}
        >
          {({ isActive: linkActive }) => {
            const on = isActive ? isActive(path) : linkActive;
            return (
              <chakra.span
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap="1"
                color={on ? "fg.onBrand" : "fg.brandSoft"}
                aria-pressed={on}
              >
                <Box h="26px" display="flex" alignItems="center">
                  <Icon name={icon} boxSize="26px" />
                </Box>
                <chakra.span
                  textStyle="navLabel"
                  fontWeight={on ? "medium" : "normal"}
                  color="inherit"
                >
                  {label}
                </chakra.span>
              </chakra.span>
            );
          }}
        </NavLink>
      ))}
    </Box>
  );
}
